<template>
  <q-page v-if="data">
    <PageBreadcrumbs
      :items="breadcrumbs"
    />
    <div class="bg-dark q-px-md q-pb-md">
      <div class="row items-center q-col-gutter-lg">
        <div class="col">
          <div class="column q-gutter-y-sm">
            <h1 class="text-h3 text-weight-medium no-margin">
              {{ brand.shortDescription ?? brand.name }}
            </h1>

            <div
              v-if="brand.description"
              class="text-body2 description"
            >
              {{ brand.description }}
            </div>
          </div>
        </div>
        <div
          v-if="brand.imageUrl"
          class="col-12 col-sm-3 col-md-2"
        >
          <div class="row flex-center">
            <q-img
              :src="brand.imageUrl"
              :placeholder-src="brand.imageUrl"
              spinner-color="primary"
              loading="eager"
              fetchpriority="high"
              fit="contain"
              style="min-width: 75px; max-height: 100px"
              no-spinner
              no-transition
            />
          </div>
        </div>
      </div>
    </div>

    <LazyBrandRebates
      v-if="brand.hasRebates"
      :id="brand.id"
    />

    <div class="q-pa-md">
      <CatalogCollection
        :query
        :name="`Manufacturer: ${brand.name}`"
        :data-key="`brand_${brand.id}_products`"
      />
    </div>
  </q-page>
</template>

<script lang="ts" setup>
import type { QBreadcrumbsElProps } from 'quasar';
import type { Brand, Product } from '~/types/search';
import type { FilterByQuery } from '~/types/typesense';

definePageMeta({
  alias: [
    '/manufacturer/:slug',
    '/manufacturer/:slug/shop',
    '/b/:slug',
    '/b/:slug/shop',
    '/brand/:slug',
    '/brand/:slug/shop',
  ],
});

const { $typesense } = useNuxtApp();
const route = useRoute();
const router = useRouter();
const { buildFieldsList, buildFilterBy } = useTypesenseUtils();

const fields = buildFieldsList<Brand>([
  'id',
  'name',
  'slug',
  'description',
  'imageUrl',
  'hasRebates',
  'metaTitle',
  'metaDescription',
  'shortDescription',
]);

const { data, error } = await useAsyncData(
  `brands_${route.params.slug}`,
  () => $typesense.first<Brand>('kygunco_brands', {
    q: '*',
    query_by: '',
    filter_by: buildFilterBy<Brand>({ slug: { $eq: route.params.slug as string } }),
    include_fields: fields,
  }, true),
  {
    getCachedData: getCachedPayload,
  },
);

if (error.value) {
  if (import.meta.server) {
    throw createError({ ...error.value });
  }

  throw showError({ ...error.value });
}

const brand = data.value as Brand;

const query: FilterByQuery<Product> = {
  manufacturer: { slug: { $eq: brand.slug } },
};

const featured = computed(() => router.hasRoute(`brand-${brand.slug}`));

const breadcrumbs = computed(() => {
  const stack: QBreadcrumbsElProps[] = [
    { label: 'Brands', to: '/brands' },
    { label: brand.name, to: `/brand/${brand.slug}`, disable: !featured.value },
  ];

  if (featured.value) {
    stack.push({
      label: 'Shop',
    });
  }

  return stack;
});

defineOgImage(brand.imageUrl && !brand.imageUrl.includes('.svg')
  ? { url: brand.imageUrl }
  : undefined);

useSeoMeta({
  title: () => brand.metaTitle ?? brand.shortDescription ?? `Shop ${brand.name}`,
  description: () => brand.metaDescription ?? brand.description,
});

// Always make sure the canonical URL set correctly
useHead({ link: [{ rel: 'canonical', href: withSiteUrl(`/brand/${brand.slug}`).value }] });

useSchemaOrg([
  {
    '@type': 'Brand',
    'name': brand.name,
    'logo': brand.imageUrl,
    'image': brand.imageUrl,
    'url': withSiteUrl(`/brand/${brand.slug}`).value,
  },
]);
</script>

<style lang="scss" scoped>
@use "sass:map";

.description {
  @media (min-width: $breakpoint-md-min) {
    font-size: map.get($body1, 'size');
    font-weight: map.get($body1, 'weight');
    line-height: map.get($body1, 'line-height');
    letter-spacing: map.get($body1, 'letter-spacing');
  }
}
</style>
